// Multi-select dropdown: a select-shaped trigger that opens a checkbox list,
// so several options can be active at once (e.g. Preconstruction +
// Construction on the Projects filter bar). Empty selection = "All".
//
// Closes on outside click or Escape. Purely controlled — the parent owns the
// Set of selected ids.

import { useEffect, useRef, useState } from 'react';

export interface MultiSelectOption {
  id: number;
  label: string;
}

export default function MultiSelect({
  label, options, selected, onToggle, onClear, minWidth = 150,
}: {
  /** Filter name shown on the trigger, e.g. "Status". */
  label: string;
  options: MultiSelectOption[];
  selected: Set<number>;
  onToggle: (id: number) => void;
  onClear: () => void;
  minWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Outside click / Escape close.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const summary = selected.size === 0
    ? `${label}: All`
    : selected.size === 1
      ? (options.find((o) => selected.has(o.id))?.label ?? `${label}: 1`)
      : `${label}: ${selected.size} selected`;

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          border: `1px solid ${selected.size > 0 ? 'var(--ew-green)' : 'var(--ew-n-200)'}`,
          borderRadius: 'var(--ew-r-md)', padding: '6px 10px',
          fontSize: 13, fontFamily: 'var(--ew-font)', cursor: 'pointer', minWidth,
          background: selected.size > 0 ? 'var(--ew-green-50)' : '#fff',
          color: selected.size > 0 ? 'var(--ew-green-700)' : 'var(--ew-dark)',
          fontWeight: selected.size > 0 ? 600 : 400,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <span aria-hidden style={{ fontSize: 10, color: 'var(--ew-n-400)' }}>▼</span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={`Filter by ${label}`}
          style={{
            position: 'absolute', zIndex: 60, top: 'calc(100% + 4px)', left: 0, minWidth: Math.max(minWidth, 190),
            background: '#fff', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)',
            boxShadow: 'var(--ew-shadow-3)', padding: '6px 0', maxHeight: 320, overflowY: 'auto',
          }}
        >
          {options.map((o) => {
            const on = selected.has(o.id);
            return (
              <label
                key={o.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                  fontSize: 13, fontFamily: 'var(--ew-font)', cursor: 'pointer', userSelect: 'none',
                  color: on ? 'var(--ew-green-700)' : 'var(--ew-dark)', fontWeight: on ? 600 : 400,
                  background: on ? 'var(--ew-green-50)' : undefined,
                }}
              >
                <input type="checkbox" checked={on} onChange={() => onToggle(o.id)} style={{ margin: 0 }} />
                {o.label}
              </label>
            );
          })}
          <div style={{ borderTop: '1px solid var(--ew-n-100)', marginTop: 4, paddingTop: 4, display: 'flex', justifyContent: 'space-between', padding: '6px 12px 2px' }}>
            <button
              type="button"
              onClick={onClear}
              disabled={selected.size === 0}
              style={{ background: 'none', border: 'none', fontSize: 12, fontWeight: 600, fontFamily: 'var(--ew-font)', cursor: selected.size ? 'pointer' : 'default', color: selected.size ? 'var(--ew-n-500)' : 'var(--ew-n-300)', textDecoration: 'underline', padding: 0 }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', fontSize: 12, fontWeight: 600, fontFamily: 'var(--ew-font)', cursor: 'pointer', color: 'var(--ew-green-700)', padding: 0 }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
